import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type TotalsBarProps = {
    totalDebit: string;
    totalCredit: string;
    differenceGel: string;
    balanced: boolean;
    hasAmounts: boolean;
};

const Total: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span
            className="text-base font-semibold tabular-nums text-foreground"
            {...getNodeTestIdProp(`journal-total-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * Totals + balance indicator — the emotional centre of the form. Shows Total
 * Debit / Total Credit and either a calm "Balanced" state or the explicit gap
 * ("Unbalanced by ₾X.XX"). Right-aligned tabular figures throughout.
 */
export const TotalsBar: React.FC<TotalsBarProps> = ({
    totalDebit,
    totalCredit,
    differenceGel,
    balanced,
    hasAmounts,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-surface-secondary px-4 py-3">
            <div className="flex items-center gap-8">
                <Total
                    label={t('journalEntry.totals.debit')}
                    slot="debit"
                    value={totalDebit}
                />
                <Total
                    label={t('journalEntry.totals.credit')}
                    slot="credit"
                    value={totalCredit}
                />
            </div>

            <div
                className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold',
                    balanced
                        ? 'bg-success-soft text-success-soft-foreground'
                        : 'bg-danger-soft text-danger-soft-foreground',
                )}
                {...getNodeTestIdProp('journal-balance-indicator')}
            >
                {balanced ? (
                    <>
                        <Icon aria-hidden icon="gravity-ui:circle-check" />
                        {t('journalEntry.totals.balanced')}
                    </>
                ) : (
                    <>
                        <Icon aria-hidden icon="gravity-ui:scales" />
                        {hasAmounts
                            ? t('journalEntry.totals.unbalancedBy', {
                                  amount: differenceGel,
                              })
                            : t('journalEntry.totals.enterAmounts')}
                    </>
                )}
            </div>
        </div>
    );
};
